import React, { useEffect, useState } from 'react'
import { Navbar, Nav, NavDropdown, Container } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { checkIsMobile } from 'shared'
import appConfig from 'app.config'
import {
	IMenuDropdownItem,
	IMenuItems,
	menuItems,
} from '../../constants/menuItems'
import { ProductType } from 'selfServicePortal/interfaces/i-api-support'
import { useSspContext } from 'selfServicePortal/hooks/useSspContext'
import {
	ICreditBalance,
	IDepositData,
	IMSAgreementStatus,
} from 'selfServicePortal/interfaces/i-api-creaditCard'
import {
	CustomerStatus,
	ICustomer,
} from 'selfServicePortal/interfaces/i-api-customer'
import SepaDirectDebit from 'selfServicePortal/containers/Home/Account/SDD/sepaDirectDebit'
import { Link } from 'react-router-dom'
import { appRoutes } from 'selfServicePortal/constants/routes'
import './Menu.scss'

interface IMenu {
	changeComponent: (item: IMenuDropdownItem) => void
	isMenuOpen: boolean
	setIsMenuOpen: (isOpen: boolean) => void
}

const Menu = ({ changeComponent, isMenuOpen, setIsMenuOpen }: IMenu) => {
	const { t } = useTranslation()
	const isMobile = checkIsMobile()
	const {
		setOverviewData,
		overviewDataList,
		overviewData,
		loading,
		typeOfAccount,
		setIsCard,
		isSddEligible,
		customerData,
		productType,
	} = useSspContext()

	const [customerDataState, setCustomerDataState] = useState<
		ICustomer | undefined
	>(undefined)

	const [agreementState, setAgreementState] = useState<
		ICreditBalance | IDepositData | undefined
	>(undefined)

	useEffect(() => {
		setCustomerDataState(customerData)
		setAgreementState(overviewData)
	}, [customerData, overviewData])

	const getCurrentProductType = () => {
		switch (productType) {
			case 'Card':
				return ProductType.Card
			case 'Deposit_Fixed':
				return ProductType.Deposit_Fixed
			default:
				return ProductType.Deposit
		}
	}

	function filterMenuItems(menuItems: IMenuItems[]) {
		const isFastrentekonto =
			typeOfAccount(overviewData?.productId).product === 'Fastrentekonto'
		const currentCountry = appConfig.country.toLowerCase()
		const currentProductType = getCurrentProductType()

		return menuItems
			.filter((menuItem) =>
				menuItem.products.includes(currentProductType),
			)
			.map((menuItem) => ({
				...menuItem,
				items: menuItem.items.filter(
					(dropDown) =>
						dropDown.countries.includes(currentCountry) &&
						dropDown.products.includes(currentProductType) &&
						!(dropDown.title === 'Withdrawal' && isFastrentekonto),
				),
			}))
	}

	const filterMenuItemsByStatus = (menuItems: IMenuItems[]): IMenuItems[] => {
		const isClosedState =
			customerDataState?.status === CustomerStatus.Closed ||
			(agreementState as IDepositData)?.agreementStatus ===
				IMSAgreementStatus.Closed
		if (isClosedState) {
			return menuItems.map((menuItem) => ({
				...menuItem,
				items:
					menuItem.title === 'Account'
						? menuItem.items.filter(
								(item): item is IMenuDropdownItem =>
									item.title === 'PaymentInformation' ||
									item.title === 'AnnualReport',
							)
						: menuItem.items,
			}))
		}

		return menuItems
	}

	const addSddMenuItem = (menuItems: IMenuItems[]): IMenuItems[] => {
		const accountMenuIndex = menuItems.findIndex(
			(item) => item.title === 'Account',
		)

		if (accountMenuIndex === -1) return menuItems

		const sddMenuItem: IMenuDropdownItem = {
			title: 'SDD',
			show: false,
			component: <SepaDirectDebit />,
			countries: [],
			products: [ProductType.Card],
		}

		const updatedMenuItems = [...menuItems]

		updatedMenuItems[accountMenuIndex] = {
			...updatedMenuItems[accountMenuIndex],
			items: [...updatedMenuItems[accountMenuIndex].items, sddMenuItem],
		}

		return updatedMenuItems
	}

	const getFilteredMenuItems = (menuItems: IMenuItems[]) => {
		let filteredItems = filterMenuItems(menuItems)
		filteredItems = filteredItems.filter((menu) => menu.items.length > 0)
		filteredItems = filterMenuItemsByStatus(filteredItems)

		if (isSddEligible?.eligible) {
			return addSddMenuItem(filteredItems)
		}
		return filteredItems
	}

	const getAgreements = () =>
		overviewDataList?.map((a) => ({
			product: typeOfAccount(a.productId).product,
			accountAgreementId: a.hasOwnProperty('cardAgreementId')
				? (a as ICreditBalance).creditCardNo
				: a.accountAgreementId,
		})) ?? []

	const setAgreement = (agreementId: string) => {
		const agreement = overviewDataList?.find((a) =>
			a.hasOwnProperty('cardAgreementId')
				? (a as ICreditBalance).creditCardNo === agreementId
				: (a as IDepositData).accountAgreementId === agreementId,
		)
		setOverviewData(agreement)
		setIsCard(typeOfAccount(agreement!.productId))
		changeComponent(menuItems[0].items[0])
	}

	const handleMenuItemClick = (item: IMenuDropdownItem) => {
		changeComponent(item)
		if (isMobile) {
			setIsMenuOpen(false)
		}
	}

	const renderProductDropdown = () => (
		<>
			<NavDropdown
				title={t('Product')}
				id="product-dropdown"
				data-cy="productDropdown">
				{getAgreements().map((agreement, i) => (
					<NavDropdown.Item
						key={i}
						onClick={() =>
							setAgreement(agreement.accountAgreementId)
						}
						data-cy={`agreementItem-${agreement.accountAgreementId}`}>
						{`${agreement.product} ${agreement.accountAgreementId}`}
					</NavDropdown.Item>
				))}
			</NavDropdown>
		</>
	)

	const renderMenuItems = () => (
		<>
			{!loading &&
				getFilteredMenuItems(menuItems).map(
					(menuItem: IMenuItems, key: number) => (
						<NavDropdown
							key={key}
							title={t(menuItem.title as string)}
							id={`nav-dropdown-${key}`}
							data-cy={`menuDropdown-${menuItem.title}`}>
							{menuItem.items.map(
								(item: IMenuDropdownItem, i: number) => (
									<NavDropdown.Item
										key={i}
										data-cy={`menuItem-${item.title}`}
										onClick={() =>
											handleMenuItemClick(item)
										}
										className="dropdownmenu-item">
										{t(item.title as string)}
									</NavDropdown.Item>
								),
							)}
						</NavDropdown>
					),
				)}

			{!loading && appConfig.country === 'no' && renderProductDropdown()}
		</>
	)

	if (isMobile) {
		return (
			<Navbar.Collapse
				id="basic-navbar-nav"
				className="mobile-menu-overlay"
				in={isMenuOpen}>
				<div className="mobile-menu-content">
					<Nav className="flex-column">
						{!loading &&
							getFilteredMenuItems(menuItems).map(
								(menuItem: IMenuItems, key: number) => (
									<NavDropdown
										key={key}
										title={t(menuItem.title as string)}
										id={`nav-dropdown-${key}`}
										data-cy={`menuDropdown-${menuItem.title}`}>
										{menuItem.items.map(
											(
												item: IMenuDropdownItem,
												i: number,
											) => (
												<NavDropdown.Item
													key={i}
													data-cy={`menuItem-${item.title}`}
													onClick={() =>
														handleMenuItemClick(
															item,
														)
													}>
													{t(item.title as string)}
												</NavDropdown.Item>
											),
										)}
									</NavDropdown>
								),
							)}

						{!loading &&
							appConfig.country === 'no' &&
							renderProductDropdown()}
						<Link
							to={appRoutes.logout}
							className="mobile-sign-out"
							data-cy="mobileSignOutLink"
							onClick={() => setIsMenuOpen(false)}>
							{t('SignOut')}
						</Link>
					</Nav>
				</div>
			</Navbar.Collapse>
		)
	}

	return (
		<Navbar expand="lg" className="menu-container">
			<Container>
				<Navbar.Toggle
					aria-controls="basic-navbar-nav"
					data-cy="menuToggle"
				/>
				<Navbar.Collapse id="basic-navbar-nav">
					<Nav className="me-auto">{renderMenuItems()}</Nav>
				</Navbar.Collapse>
			</Container>
		</Navbar>
	)
}

export default Menu
